import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCheck, FiEdit2, FiLoader, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import {
  cChannel, cChannelPosts, cChannelSubscribers, cDeleteChannel, cRemoveSubscriber,
  cSetSubscriberRole, cSubscribeChannel, cUpdateChannel, cUpload, fullUrl,
} from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import PostCard from '../../components/community/PostCard';
import PostComposer from '../../components/community/PostComposer';
import UserAvatar from '../../components/community/UserAvatar';
import { BLUE, BLUE_GRADIENT, compactNumber } from '../../components/community/brand';

const PAGE = 10;

/**
 * One channel: banner, about, the subscribe (“ជាវ”) button, the owner's tools
 * (rename, nickname, about, approve the waiting subscribers, delete) and the
 * articles published inside it.
 */
export default function ChannelPage() {
  const { ref } = useParams();
  const { t } = useLanguage();
  const { user, requireAuth, openAuth } = useCommunity();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', nickname: '', about: '', avatar: '', cover: '' });
  const [imgBusy, setImgBusy] = useState('');   // 'avatar' | 'cover' | ''

  const load = () => {
    setLoading(true);
    cChannel(ref)
      .then((ch) => {
        setChannel(ch);
        setForm({
          name: ch.name,
          nickname: ch.nickname || '',
          about: ch.about || '',
          avatar: ch.avatar || '',
          cover: ch.cover || '',
        });
        return cChannelPosts(ref, { page: 1, limit: PAGE }).catch(() => null);
      })
      .then((res) => {
        if (res) { setPosts(res.items || []); setPages(res.pages || 1); setPage(1); }
      })
      .catch((err) => toast.error(err?.response?.data?.detail || t('somethingWrong')))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [ref, user]);

  const loadSubs = () => cChannelSubscribers(ref)
    .then((r) => setSubs(r.items || []))
    .catch(() => {});

  useEffect(() => {
    if (channel && channel.is_owner) loadSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const loadMore = () => {
    const next = page + 1;
    cChannelPosts(ref, { page: next, limit: PAGE }).then((res) => {
      setPosts((prev) => [...prev, ...(res.items || [])]);
      setPage(next);
      setPages(res.pages || 1);
    }).catch(() => {});
  };

  const subscribe = async () => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    try {
      const res = await cSubscribeChannel(ref);
      toast.success(res.subscribed ? t('subscribed') : res.pending ? t('subscriberPending') : t('done'));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  /** Upload the channel profile picture () or the cover (saved with “Save”). */
  const pickImg = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(field);
    try {
      const res = await cUpload([file]);
      setForm((f) => ({ ...f, [field]: res.urls?.[0] || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setImgBusy('');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const res = await cUpdateChannel(ref, {
        name: form.name.trim(),
        nickname: form.nickname.trim(),
        about: form.about.trim(),
        avatar: form.avatar || '',
        cover: form.cover || '',
      });
      setChannel(res.channel);
      setEditing(false);
      toast.success(t('updatedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const approve = async (member, role) => {
    try {
      await cSetSubscriberRole(ref, member.user_id, role);
      toast.success(role === 'subscriber' ? t('approve') : t('updatedOk'));
      loadSubs();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const dropSub = async (member) => {
    if (!window.confirm(`${t('remove')} @${member.user?.username}?`)) return;
    try {
      await cRemoveSubscriber(ref, member.user_id);
      toast.success(t('deletedOk'));
      loadSubs();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const remove = async () => {
    if (!window.confirm(t('channelDeleteConfirm'))) return;
    try {
      await cDeleteChannel(ref);
      toast.success(t('deletedOk'));
      navigate('/channels');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  if (loading && !channel) {
    return <p className="mx-auto max-w-[1100px] px-4 py-6 text-sm text-gray-400">{t('loading')}</p>;
  }
  if (!channel) {
    return (
      <div className="mx-auto max-w-[1100px] px-4 py-10 text-center">
        <p className="text-sm text-gray-500">{t('somethingWrong')}</p>
        <Link to="/channels" className="mt-3 inline-block font-bold" style={{ color: BLUE }}>{t('channelsNav')}</Link>
      </div>
    );
  }

  const input = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm outline-none focus:border-gray-400';

  return (
    <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-4">
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div
          className="h-32"
          style={{ background: channel.cover ? `url(${fullUrl(channel.cover)}) center/cover` : BLUE_GRADIENT }}
        />
        <div className="px-4 pb-4">
          <div className="flex items-end gap-3 -mt-10">
            {channel.avatar ? (
              <img src={fullUrl(channel.avatar)} alt={channel.name} className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white dark:ring-gray-900" />
            ) : (
              <span
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold ring-4 ring-white dark:ring-gray-900"
                style={{ background: BLUE }}
              >
                {(channel.name || 'C').slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1 pb-1">
              <h1 className="font-extrabold text-lg truncate">{channel.name}</h1>
              <p className="text-[11px] text-gray-500 truncate">
                @{channel.nickname || channel.slug} · {compactNumber(channel.subscriber_count)} {t('subscribers')}
                {' · '}{compactNumber(channel.post_count)} {t('articles')}
              </p>
            </div>
            {channel.is_owner ? (
              <div className="flex items-center gap-2 pb-1">
                <button
                  type="button"
                  onClick={() => setEditing((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-gray-100 dark:bg-gray-800"
                >
                  <FiEdit2 className="w-3.5 h-3.5" /> {t('channelEdit')}
                </button>
                <button
                  type="button"
                  onClick={remove}
                  className="p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-gray-800"
                  aria-label={t('delete')}
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => (user ? subscribe() : openAuth('signin', t('loginRequiredDesc')))}
                className={`mb-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold ${
                  channel.subscribed || channel.pending
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                    : 'text-white'
                }`}
                style={channel.subscribed || channel.pending ? undefined : { background: BLUE }}
              >
                {channel.subscribed ? <FiCheck className="w-4 h-4" /> : <FiPlus className="w-4 h-4" />}
                {channel.subscribed ? t('subscribed') : channel.pending ? t('subscriberPending') : t('subscribe')}
              </button>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px] font-bold">
            <span className={`px-2 py-0.5 rounded-full ${channel.is_public ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
              {channel.is_public ? t('channelPublic') : t('channelPrivate')}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
              {channel.is_open ? t('channelOpen') : t('channelApproval')}
            </span>
            {channel.is_owner && channel.pending_subscribers > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                {channel.pending_subscribers} {t('subscriberPending')}
              </span>
            )}
          </div>

          {channel.about && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{channel.about}</p>
          )}

          {editing && (
            <form onSubmit={save} className="mt-3 space-y-2 anim-pop">
              {/* រូបភាពប្រវត្តិរូប + រូបភាពគម្របឆានែល */}
              <div className="grid sm:grid-cols-[auto,1fr] gap-3">
                <div>
                  <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('channelProfile')}</span>
                  {form.avatar ? (
                    <img src={fullUrl(form.avatar)} alt="" className="w-16 h-16 rounded-xl object-cover mb-1.5" />
                  ) : (
                    <span className="block w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 mb-1.5" />
                  )}
                  <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-[11px] font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                    <input type="file" accept="image/*" hidden onChange={(e) => pickImg(e, 'avatar')} />
                    {imgBusy === 'avatar' && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                    {t('channelUploadProfile')}
                  </label>
                </div>
                <div>
                  <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('channelCover')}</span>
                  {form.cover ? (
                    <img src={fullUrl(form.cover)} alt="" className="w-full h-24 rounded-xl object-cover mb-1.5" />
                  ) : (
                    <span className="block w-full h-24 rounded-xl mb-1.5" style={{ background: BLUE_GRADIENT }} />
                  )}
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-[11px] font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                      <input type="file" accept="image/*" hidden onChange={(e) => pickImg(e, 'cover')} />
                      {imgBusy === 'cover' && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                      {t('channelUploadCover')}
                    </label>
                    {form.cover && (
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, cover: '' }))}
                        className="text-[11px] font-bold text-red-500 hover:underline"
                      >
                        {t('remove')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-2">
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('channelName')} className={input} />
                <input value={form.nickname} onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))} placeholder={t('channelNickname')} className={input} />
              </div>
              <textarea value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={3} placeholder={t('channelAbout')} className={input} />
              <div className="flex items-center gap-2">
                <button type="submit" className="px-4 py-2 rounded-full text-white text-sm font-bold" style={{ background: BLUE }}>{t('save')}</button>
                <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 rounded-full text-sm font-bold bg-gray-100 dark:bg-gray-800">{t('cancel')}</button>
              </div>
            </form>
          )}
        </div>
      </section>

      {channel.is_owner && subs.some((s) => s.role === 'pending') && (
        <section className="mt-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <h2 className="font-extrabold text-sm mb-2">{t('subscriberPending')}</h2>
          <ul className="space-y-2">
            {subs.filter((s) => s.role === 'pending').map((s) => (
              <li key={s.id} className="flex items-center gap-3">
                <UserAvatar user={s.user} size={34} />
                <span className="min-w-0 flex-1 text-sm font-semibold truncate">
                  {s.user?.name || s.user?.username}
                </span>
                <button
                  type="button"
                  onClick={() => approve(s, 'subscriber')}
                  className="px-3 py-1.5 rounded-full text-xs font-bold text-white"
                  style={{ background: BLUE }}
                >
                  {t('approve')}
                </button>
                <button
                  type="button"
                  onClick={() => dropSub(s)}
                  className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-gray-800"
                  aria-label={t('remove')}
                >
                  <FiX className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <h2 className="mt-4 mb-1 font-extrabold text-sm">{t('channelPosts')}</h2>
      <p className="text-[11px] text-gray-400 mb-3">{t('postToChannel')}</p>

      {posts.length === 0 ? (
        <p className="text-sm text-gray-400">{t('noArticles')}</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onChanged={() => load()}
              onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}

      {page < pages && (
        <button
          type="button"
          onClick={loadMore}
          className="mt-3 w-full py-2.5 rounded-xl text-sm font-bold border-2"
          style={{ borderColor: BLUE, color: BLUE }}
        >
          {t('viewAll')}
        </button>
      )}

      {channel.is_owner && (
        <div className="mt-4">
          <PostComposer asChannel={channel} onSaved={() => load()} />
        </div>
      )}
    </div>
  );
}